import { mergeClasses, Switch } from "@fluentui/react-components";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { MapRef } from "react-map-gl/maplibre";
import { LayoutContext } from "../../../context/LayoutContext";
import { useApiCall } from "../../../hooks/useApiCall";
import { useMapSas } from "../../../hooks/useMapSas";
import {
  IAirportMap,
  IFlightMarker,
} from "../../../models/airports/AirportConfig";
import { AIRPORT_CODE_PARAMETER } from "../../../utils/common/Constants";
import { mapApiUrl } from "../../../utils/maps/MapConstants";
import { getAzureMapTileUrl } from "../../../utils/maps/MapHelperFunctions";
import DisplayMapErrors from "../../osm-maps/DisplayMapErrors";
import MapLibreMap from "../core/MapLibreMap";
import { useMapFailures } from "../hooks/useMapFailures";
import AirportViewControls from "../map-controls/AirportViewControls";
import AirportMarker from "../map-markers/AirportMarker";
import FlightMarker from "../map-markers/flight-markers/FlightMarker";
import SelectedFlightMarker from "../map-markers/flight-markers/SelectedFlightMarker";
import { isValidLatLng, toLngLat } from "../utils/coordinates";
import MapResources from "./MapResources";
import {
  mapDisplayErrorStyles,
  mapLibreControlsStyles,
  mapLibreStyles,
} from "./mapStyles";

export interface MapDisplayFlightsProps {
  airportMapDetail: IAirportMap;
  setSideBarActive: (isActive: boolean) => void;
}

const flightTrackingEndPoint = process.env.REACT_APP_API_FLIGHT_TRACKING;
const baseURL = process.env.REACT_APP_API_BASE_URL;
const flightPositionsUrl = `${baseURL}${flightTrackingEndPoint}`;

const MapDisplayFlights: React.FC<MapDisplayFlightsProps> = ({
  airportMapDetail,
  setSideBarActive,
}) => {
  const styles = mapLibreStyles();
  const controlStyles = mapLibreControlsStyles();
  const errorStyles = mapDisplayErrorStyles();
  const mapRef = useRef<MapRef>(null);
  const [showWeather, setShowWeather] = useState(false);
  const [showFlights, setShowFlights] = useState(true);
  const [showGates, setShowGates] = useState(false);
  const [showRSC, setShowRSC] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  const { selectedFlight, flightFilterParams, setSelectedFlight } =
    useContext(LayoutContext);
  const { mapApiFailures, handleMapApiFailure } = useMapFailures();

  const {
    sas: mapSas,
    error: mapSasError,
    forceRefresh: refreshMapSas,
    needsReset: mapSasNeedsReset,
    acknowledgeReset: acknowledgeMapSasReset,
  } = useMapSas(airportMapDetail.id);

  const azureBaseMapUrl = getAzureMapTileUrl(
    airportMapDetail.id,
    mapApiUrl,
    "BASE",
    mapSas
  );
  const azureWeatherMapUrl = getAzureMapTileUrl(
    airportMapDetail.id,
    mapApiUrl,
    "WEATHER",
    mapSas
  );

  let urlWithParams = flightPositionsUrl?.replace(
    AIRPORT_CODE_PARAMETER,
    airportMapDetail.id
  );
  urlWithParams += `?legType=${flightFilterParams.legType}`;
  if (flightFilterParams.flightIDs.length > 0) {
    urlWithParams += `&FlightNumbers=${flightFilterParams.flightIDs.join(",")}`;
  }

  const { data: flightData } = useApiCall<IFlightMarker[]>({
    url: urlWithParams,
    dependencies: [airportMapDetail.id, flightFilterParams],
    poll: 30,
  });

  const selectedFlightMarkerDetails = useMemo(
    () =>
      flightData?.find((flight) =>
        flight.docId
          ? selectedFlight.flightDocID === flight.docId
          : selectedFlight.flightID === flight.flightNumber
      ),
    [flightData, selectedFlight.flightDocID, selectedFlight.flightID]
  );
  const visibleFlights = useMemo(
    () =>
      flightData?.filter(
        (flight) =>
          flight !== selectedFlightMarkerDetails &&
          isValidLatLng([flight.latitude, flight.longitude])
      ) ?? [],
    [flightData, selectedFlightMarkerDetails]
  );

  const handleMarkerClick = useCallback(
    (flightID: string, docId: string) => {
      setSelectedFlight({ flightDocID: docId, flightID });
      setSideBarActive(true);
    },
    [setSelectedFlight, setSideBarActive]
  );

  const handleViewChange = useCallback(
    (zoom: number) => {
      if (!isValidLatLng(airportMapDetail.location)) {
        return;
      }
      mapRef.current?.flyTo({
        center: toLngLat(airportMapDetail.location),
        zoom,
        duration: 1000,
      });
    },
    [airportMapDetail.location]
  );

  useEffect(() => {
    handleMapApiFailure("SAS", !mapSasError);
  }, [mapSasError, handleMapApiFailure]);

  useEffect(() => {
    if (mapSasNeedsReset) {
      setMapKey((value) => value + 1);
      acknowledgeMapSasReset();
    }
  }, [mapSasNeedsReset, acknowledgeMapSasReset]);

  useEffect(() => {
    if (!isValidLatLng(airportMapDetail.location)) {
      return;
    }
    mapRef.current?.flyTo({
      center: toLngLat(airportMapDetail.location),
      zoom: mapRef.current.getZoom(),
      duration: 0,
    });
  }, [airportMapDetail.location]);

  return (
    <section
      className={styles.mapContainer}
      data-testid="maplibre-flight-tracking-map"
    >
      <MapLibreMap
        key={mapKey}
        mapRef={mapRef}
        center={airportMapDetail.location}
        baseMapUrl={mapSas ? azureBaseMapUrl : undefined}
        weatherMapUrl={mapSas ? azureWeatherMapUrl : undefined}
        showWeather={showWeather}
        onMapApiFailure={handleMapApiFailure}
        onAuthError={refreshMapSas}
        testId="maplibre-map-canvas"
      >
        <section
          className={styles.mapControlContainer}
          data-testid="custom-map-controls"
        >
          <div
            className={mergeClasses(
              controlStyles.cardContainer,
              controlStyles.toggleControls
            )}
          >
            <Switch
              checked={showWeather}
              onChange={(_event, data) => setShowWeather(data.checked)}
              label="Precipitation"
            />
            <Switch
              checked={showFlights}
              onChange={(_event, data) => setShowFlights(data.checked)}
              label="Flights"
            />
            <Switch
              checked={showGates}
              onChange={(_event, data) => setShowGates(data.checked)}
              label="Stands"
            />
            <Switch
              checked={showRSC}
              onChange={(_event, data) => setShowRSC(data.checked)}
              label="RSC"
            />
          </div>
          <AirportViewControls onViewChange={handleViewChange} />
        </section>

        <AirportMarker
          code={airportMapDetail.code}
          name={airportMapDetail.name}
          position={airportMapDetail.location}
          onClick={() => handleViewChange(13)}
        />

        {showFlights &&
          visibleFlights.map((flight) => (
            <FlightMarker
              key={flight.id}
              airportCode={airportMapDetail.code}
              flightNumber={flight.flightNumber}
              direction={flight.direction}
              position={[flight.latitude, flight.longitude]}
              flightStatus={flight.status}
              legType={flight.legType}
              docID={flight.docId}
              selectedFlight={false}
              acType={flight.aircraftType}
              acReg={flight.aircraftRegistration}
              onBlockTime={flight.bestInBlockTime}
              offBlockTime={flight.bestOffBlockTime}
              updateMarkerId={handleMarkerClick}
            />
          ))}

        {showFlights && selectedFlightMarkerDetails && (
          <SelectedFlightMarker
            selectedFlightMarkerDetails={selectedFlightMarkerDetails}
            airportCode={airportMapDetail.code}
            handleMarkerClick={handleMarkerClick}
          />
        )}

        <MapResources
          airportId={airportMapDetail.id}
          showGates={showGates}
          showRSC={showRSC}
          setMapApiFailures={handleMapApiFailure}
        />

        <DisplayMapErrors
          messages={mapApiFailures}
          className={errorStyles.flightMap}
        />
      </MapLibreMap>
    </section>
  );
};

export default MapDisplayFlights;
